// Native draw.io (mxGraph) cell builders. These produce *editable* mxCell XML
// fragments (rectangles, text, images, edges, tables) that visually mirror the
// CAD-styled SVG export in svgExport.js, but are fully movable/editable inside
// draw.io. All decorative frame cells are grouped under a single container that
// carries netappFrame="1" so the SwitchR importer can skip the whole group
// (children use parent != "1" and are ignored by the existing import logic).
import { iconDataUri, deviceLabel } from './deviceIcons';
import { connDrawioStyle, connLabel, connType } from './connections';
import { subnetInfo } from './subnet';
import {
  computeDetailModel,
  DLAYOUT,
  detailBoxHeight,
  detailPortCenterY,
  detailRowHeight,
  detailRowFont,
  deviceDisplayName,
  portNameGlobal,
  getAllEdges,
  findDeviceGlobal,
} from './detail';
import { computePanelLayout, pickBothSidesSide, P, wrapText } from './panel';
import { resolvePortVlans } from './vlanPorts';
import { SHEET, sortDevicesByIp, deviceIpEntries, sortEntriesByIp } from './svgExport';
import { safeImageHref } from './safeUri';
import { pageDisplayName } from './pageNames';

// ---- shared constants (mirror svgExport.js) -------------------------------
const OUTER = 26;
const INNER = 40;
const INK = '#111418';
const MUTED = '#5b6672';
const FRAME = '#1b232c';
const SANS = 'IBM Plex Sans';
const MONO = 'IBM Plex Mono';
const WARN = '#B45309'; // amber-700: Design-Check Warnung / Konflikt

// ---- helpers --------------------------------------------------------------
export function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function makeIdGen() {
  let n = 0;
  return (prefix) => `${prefix}_${(n++).toString(36)}`;
}

// raster (base64) data URIs break mxGraph style strings (';base64,' contains the
// style delimiter ';'). url-encoded SVG data URIs and http(s) URLs are already
// style-safe. Any other data URI (e.g. a base64 PNG/JPEG logo) is wrapped into a
// url-encoded SVG so the ';base64,' is percent-encoded (can never break the
// style) and draw.io still renders it natively — the same approach the app uses
// for device icons.
export function safeImageUri(uri) {
  // Reject anything that is not a plain image data URI / http(s) URL (XSS /
  // protocol-smuggling guard) BEFORE it reaches a style string or SVG wrapper.
  uri = safeImageHref(uri);
  if (!uri) return '';
  if (uri.startsWith('data:image/svg+xml,')) return uri;
  if (uri.startsWith('http')) {
    // ';' is the mxGraph style delimiter — an http URL containing one would
    // corrupt the cell style, so wrap it like a raster data URI instead.
    if (!uri.includes(';')) return uri;
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="100" height="100">` +
    `<image width="100" height="100" preserveAspectRatio="xMidYMid meet" xlink:href="${esc(uri)}"/></svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

function vcell(id, x, y, w, h, style, parent, value = '') {
  return `<mxCell id="${id}" value="${esc(value)}" style="${style}" vertex="1" parent="${parent}"><mxGeometry x="${x}" y="${y}" width="${w}" height="${h}" as="geometry"/></mxCell>`;
}

function imageCell(id, x, y, w, h, uri, parent) {
  const safe = safeImageUri(uri);
  if (!safe) return '';
  const style = `shape=image;imageAspect=0;aspect=fixed;image=${safe};`;
  return `<mxCell id="${id}" value="" style="${esc(style)}" vertex="1" parent="${parent}"><mxGeometry x="${x}" y="${y}" width="${w}" height="${h}" as="geometry"/></mxCell>`;
}

function lineEdge(id, x1, y1, x2, y2, style, parent) {
  return `<mxCell id="${id}" value="" style="${style}" edge="1" parent="${parent}"><mxGeometry relative="1" as="geometry"><mxPoint x="${x1}" y="${y1}" as="sourcePoint"/><mxPoint x="${x2}" y="${y2}" as="targetPoint"/></mxGeometry></mxCell>`;
}

function connectEdge(id, style, source, target, parent = '1') {
  return `<mxCell id="${id}" value="" style="${style}" edge="1" parent="${parent}" source="${source}" target="${target}"><mxGeometry relative="1" as="geometry"/></mxCell>`;
}

function tableCell(id, x, w, h, style, value, parent) {
  return `<mxCell id="${id}" value="${esc(value)}" style="${style}" vertex="1" parent="${parent}"><mxGeometry x="${x}" y="0" width="${w}" height="${h}" as="geometry"><mxRectangle width="${w}" height="${h}" as="alternateBounds"/></mxGeometry></mxCell>`;
}

function tbLabels(lang) {
  if (lang === 'en') {
    return { projekt: 'PROJECT', titel: 'TITLE', znr: 'DRAWING NO.', datum: 'DATE', blatt: 'SHEET', ersteller: 'DRAWN', geprueft: 'CHECKED', rev: 'REV.' };
  }
  return { projekt: 'PROJEKT', titel: 'TITEL', znr: 'ZEICHNUNGSNR.', datum: 'DATUM', blatt: 'BLATT', ersteller: 'ERSTELLER', geprueft: 'GEPR\u00dcFT', rev: 'REV.' };
}

export function usedConnTypes(doc) {
  const set = new Set();
  (doc.pages || []).forEach((p) => (p.edges || []).forEach((e) => set.add(e.connType || 'ethernet')));
  const arr = Array.from(set);
  return arr.length ? arr : ['ethernet', 'lwl'];
}

// MASTER devices only — alias placements (clones) are skipped so a device that
// appears on several pages is aggregated exactly once.
function allDevices(doc) {
  const list = [];
  (doc.pages || []).forEach((p) =>
    (p.nodes || []).forEach((n) => {
      if (n.data?.aliasOf) return;
      list.push({ ...n, _page: p.name });
    })
  );
  return list;
}

function detailLegendTypes(model) {
  const set = new Set();
  model.internalEdges.forEach((e) => set.add(e.connType || 'ethernet'));
  model.stubs.forEach((s) => set.add(s.connType || 'ethernet'));
  return Array.from(set);
}

// Map each port to the VLAN colour of the peer device it connects to (mirrors
// the patch-panel logic in svgExport.js).
function portPeerVlanColors(node, doc) {
  const map = {};
  if (!node || !doc) return map;
  getAllEdges(doc).forEach((e) => {
    let portId = null;
    let peerId = null;
    if (e.source === node.id && e.sourcePortId) {
      portId = e.sourcePortId;
      peerId = e.target;
    } else if (e.target === node.id && e.targetPortId) {
      portId = e.targetPortId;
      peerId = e.source;
    }
    if (!portId) return;
    const peer = findDeviceGlobal(doc, peerId);
    const v = peer && (doc.vlans || []).find((x) => x.id === peer.data?.vlanRef);
    const color = v ? v.color : null;
    if (!(portId in map)) map[portId] = color;
    else if (map[portId] !== color) map[portId] = null;
  });
  return map;
}

// ---- CAD frame (border + heading + legend + title block) ------------------
function titleBlockCells(doc, lang, pageInfo, idgen, parent) {
  const tb = doc.titleBlock || {};
  const L = tbLabels(lang);
  const tbW = 560;
  const tbH = 168;
  const tbX = SHEET.w - INNER - tbW; // 987
  const tbY = SHEET.h - INNER - tbH; // 915
  const logoW = 170;
  const infoX = tbX + logoW; // 1157
  const infoW = tbW - logoW; // 390
  const rowH = tbH / 4; // 42
  let c = '';

  // outer container (white)
  c += vcell(idgen('tb'), tbX, tbY, tbW, tbH, `rounded=0;html=1;fillColor=#ffffff;strokeColor=${FRAME};strokeWidth=1.6;`, parent);

  // logo column: optional logo image + firma name
  if (tb.logo) {
    c += imageCell(idgen('tblogo'), tbX + 14, tbY + 14, logoW - 28, tbH - 54, tb.logo, parent);
  }
  c += lineEdge(idgen('tbline'), tbX, tbY + tbH - 30, infoX, tbY + tbH - 30, `endArrow=none;html=1;strokeColor=${FRAME};strokeWidth=0.8;`, parent);
  c += vcell(idgen('tbfirma'), tbX, tbY + tbH - 30, logoW, 30, `text;html=1;align=center;verticalAlign=middle;fontSize=12;fontColor=${INK};fontFamily=${MONO};`, parent, tb.firma || '');

  const field = (x, y, w, label, value, mono) => {
    const valFont = mono ? `${MONO},monospace` : `${SANS},sans-serif`;
    // label/value are user text rendered as HTML inside draw.io -> HTML-escape
    // them so '<', '&' etc. show literally (and can never inject markup).
    const html = `<span style="font-size:9px;letter-spacing:0.6px;color:${MUTED}">${esc(label)}</span><br><span style="font-size:13px;color:${INK};font-family:${valFont}">${esc(value || '')}</span>`;
    return vcell(idgen('tbf'), x, y, w, rowH, `rounded=0;whiteSpace=wrap;html=1;fillColor=none;strokeColor=${FRAME};strokeWidth=1;align=left;verticalAlign=top;spacingLeft=8;spacingTop=4;spacingRight=4;fontFamily=${SANS};`, parent, html);
  };
  c += field(infoX, tbY, infoW, L.projekt, tb.projekt, false);
  c += field(infoX, tbY + rowH, infoW, L.titel, tb.titel, false);
  c += field(infoX, tbY + 2 * rowH, 160, L.znr, tb.zeichnungsnummer, true);
  c += field(infoX + 160, tbY + 2 * rowH, 110, L.datum, tb.datum, true);
  c += field(infoX + 270, tbY + 2 * rowH, 120, L.blatt, `${pageInfo.index} / ${pageInfo.total}`, true);
  c += field(infoX, tbY + 3 * rowH, 130, L.ersteller, tb.ersteller, false);
  c += field(infoX + 130, tbY + 3 * rowH, 130, L.geprueft, tb.geprueft, false);
  c += field(infoX + 260, tbY + 3 * rowH, 130, L.rev, tb.revision, true);
  return c;
}

// Builds the CAD frame as a single group container (netappFrame="1"). extraAttrs
// lets detail/auto pages also tag the group with netappDetail / netappAuto so the
// importer skips the whole generated page.
export function frameCells(doc, lang, pageInfo, heading, legendTypes, idgen, extraAttrs = '') {
  const gid = idgen('frame');
  const p = gid;
  let c =
    `<object id="${gid}" netappFrame="1"${extraAttrs ? ' ' + extraAttrs : ''} label="">` +
    `<mxCell style="group;connectable=0;container=0;" vertex="1" parent="1"><mxGeometry x="0" y="0" width="${SHEET.w}" height="${SHEET.h}" as="geometry"/></mxCell>` +
    `</object>`;

  // borders
  c += vcell(idgen('bo'), OUTER, OUTER, SHEET.w - 2 * OUTER, SHEET.h - 2 * OUTER, `rounded=0;html=1;fillColor=none;strokeColor=${FRAME};strokeWidth=2;`, p);
  c += vcell(idgen('bi'), INNER, INNER, SHEET.w - 2 * INNER, SHEET.h - 2 * INNER, `rounded=0;html=1;fillColor=none;strokeColor=${FRAME};strokeWidth=1;`, p);

  // heading + underline
  if (heading) {
    c += vcell(idgen('hd'), INNER + 16, INNER + 6, 700, 32, `text;html=1;align=left;verticalAlign=middle;fontSize=20;fontStyle=1;fontColor=${INK};fontFamily=${SANS};`, p, heading);
    c += lineEdge(idgen('hl'), INNER + 16, INNER + 42, INNER + 360, INNER + 42, `endArrow=none;html=1;strokeColor=${FRAME};strokeWidth=1;`, p);
  }

  // legend
  if (legendTypes && legendTypes.length) {
    const rowH = 20;
    const w = 236;
    const h = 30 + legendTypes.length * rowH;
    const lx = INNER + 12;
    const ly = SHEET.h - INNER - h - 8;
    c += vcell(idgen('lg'), lx, ly, w, h, `rounded=1;arcSize=6;html=1;fillColor=#ffffff;strokeColor=${FRAME};strokeWidth=1;`, p);
    c += vcell(idgen('lgt'), lx + 10, ly + 4, w - 20, 18, `text;html=1;align=left;verticalAlign=middle;fontSize=11;fontStyle=1;fontColor=${INK};fontFamily=${SANS};`, p, lang === 'en' ? 'LEGEND' : 'LEGENDE');
    legendTypes.forEach((key, i) => {
      const ry = ly + 30 + i * rowH + 8;
      c += lineEdge(idgen('lgl'), lx + 12, ry, lx + 54, ry, `endArrow=none;html=1;rounded=0;${connDrawioStyle(key)}`, p);
      c += vcell(idgen('lgn'), lx + 62, ry - 9, w - 72, 16, `text;html=1;align=left;verticalAlign=middle;fontSize=11;fontColor=${INK};fontFamily=${SANS};`, p, connLabel(key, lang));
    });
  }

  // title block
  c += titleBlockCells(doc, lang, pageInfo, idgen, p);
  return c;
}

// ---- Detail page: LIST view (devices with all ports as table-like boxes) ---
function detailListCells(doc, page, lang, pageInfo, idgen) {
  const model = computeDetailModel(doc, page);
  let c = frameCells(doc, lang, pageInfo, pageDisplayName(page.name, lang), detailLegendTypes(model), idgen, `netappDetail="${esc(page.id)}"`);
  const portCell = {};

  model.devices.forEach((d) => {
    const x = Math.round(d.position.x);
    const y = Math.round(d.position.y);
    const w = DLAYOUT.BOX_W;
    const rowH = detailRowHeight(d.ports.length);
    const pFont = detailRowFont(rowH);
    const h = detailBoxHeight(d.ports.length, rowH);
    const cont = idgen('ddev');
    const name = d.node.data?.label || deviceLabel(d.node.data?.deviceType, lang);
    const headerHtml = `<img src='${iconDataUri(d.node.data?.deviceType)}' width='22' height='22' style='vertical-align:middle;margin-right:6px'/>${esc(name)}`;
    c += `<mxCell id="${cont}" value="${esc(headerHtml)}" style="shape=swimlane;startSize=${DLAYOUT.HEADER_H};rounded=1;arcSize=4;html=1;whiteSpace=wrap;fillColor=#ffffff;strokeColor=${FRAME};fontSize=13;fontStyle=1;fontColor=${INK};fontFamily=${SANS};align=left;verticalAlign=middle;spacingLeft=6;swimlaneFillColor=#ffffff;" vertex="1" parent="1"><mxGeometry x="${x}" y="${y}" width="${w}" height="${h}" as="geometry"/></mxCell>`;

    if (!d.ports.length) {
      c += vcell(idgen('nop'), 0, DLAYOUT.HEADER_H, w, rowH, `text;html=1;align=left;verticalAlign=middle;spacingLeft=12;fontSize=10;fontColor=${MUTED};fontFamily=${SANS};`, cont, lang === 'en' ? 'No ports' : 'Keine Ports');
    } else {
      d.ports.forEach((pt, i) => {
        const pid = idgen('dport');
        portCell[d.deviceId + ':' + pt.id] = pid;
        const ry = DLAYOUT.HEADER_H + i * rowH;
        c += vcell(pid, 0, ry, w, rowH, `text;html=1;align=center;verticalAlign=middle;fontSize=${pFont};fontColor=#2A3138;fontFamily=${MONO};strokeColor=none;fillColor=none;`, cont, pt.name);
        if (i > 0) c += lineEdge(idgen('drow'), 0, ry, w, ry, `endArrow=none;html=1;strokeColor=${FRAME};strokeWidth=1;opacity=12;`, cont);
      });
    }
  });

  const linkStyle = (ct) => `edgeStyle=orthogonalEdgeStyle;rounded=0;html=1;endArrow=none;exitX=1;exitY=0.5;exitDx=0;exitDy=0;entryX=0;entryY=0.5;entryDx=0;entryDy=0;${connDrawioStyle(ct)}`;

  model.internalEdges.forEach((e) => {
    const s = portCell[e.sourceDeviceId + ':' + e.sourcePortId];
    const t = portCell[e.targetDeviceId + ':' + e.targetPortId];
    if (s && t) c += connectEdge(idgen('de'), linkStyle(e.connType), s, t);
  });

  if (page.showStubs) {
    model.stubs.forEach((st) => {
      const s = portCell[st.deviceId + ':' + st.portId];
      const d = model.devices.find((x) => x.deviceId === st.deviceId);
      if (!s || !d) return;
      const i = d.ports.findIndex((pt) => pt.id === st.portId);
      if (i < 0) return;
      const sx = Math.round(d.position.x) + DLAYOUT.BOX_W;
      const sy = Math.round(d.position.y) + detailPortCenterY(i, detailRowHeight(d.ports.length));
      const ex = sx + 64;
      const oName = deviceDisplayName(doc, st.otherDeviceId, lang);
      const oPort = portNameGlobal(doc, st.otherDeviceId, st.otherPortId);
      const bw = 134;
      const bh = oPort ? 32 : 20;
      const boxId = idgen('stub');
      const html = `<span style="font-size:10px;font-weight:600;color:${INK}">${esc(oName)}</span>` + (oPort ? `<br><span style="font-size:9px;color:${MUTED};font-family:${MONO},monospace">${esc(oPort)}</span>` : '');
      c += vcell(boxId, ex, sy - bh / 2, bw, bh, `rounded=1;arcSize=8;html=1;dashed=1;dashPattern=3 2;fillColor=#ffffff;strokeColor=${MUTED};strokeWidth=1;align=left;verticalAlign=middle;spacingLeft=8;fontFamily=${SANS};`, '1', html);
      c += connectEdge(idgen('se'), linkStyle(st.connType), s, boxId);
    });
  }
  return c;
}

// ---- Detail page: PANEL view (front-panel port matrix) ---------------------
function detailPanelCells(doc, page, lang, pageInfo, idgen) {
  const model = computeDetailModel(doc, page);
  let c = frameCells(doc, lang, pageInfo, pageDisplayName(page.name, lang), detailLegendTypes(model), idgen, `netappDetail="${esc(page.id)}"`);
  const layouts = {};
  model.devices.forEach((d) => (layouts[d.deviceId] = computePanelLayout(d.node)));
  const portCell = {};
  const sideOf = {};

  model.devices.forEach((d) => {
    const L = layouts[d.deviceId];
    const ox = Math.round(d.position.x);
    const oy = Math.round(d.position.y);
    const w = L.width;
    const h = L.height;
    const lw = L.labelW;
    const cont = idgen('pdev');
    c += vcell(cont, ox, oy, w, h, `rounded=1;arcSize=4;html=1;fillColor=#ffffff;strokeColor=${FRAME};strokeWidth=1.3;container=1;collapsible=0;verticalAlign=top;`, '1');
    c += vcell(idgen('lbg'), 1, 1, lw - 1, h - 2, `rounded=1;arcSize=8;html=1;fillColor=#f7f7f6;strokeColor=none;`, cont);
    c += lineEdge(idgen('pdiv'), lw, 0, lw, h, `endArrow=none;html=1;strokeColor=${FRAME};strokeWidth=1;opacity=22;`, cont);
    c += imageCell(idgen('pic'), 8, P.TOP_PAD, P.ICON, P.ICON, iconDataUri(d.node.data?.deviceType), cont);

    const vlan = (doc.vlans || []).find((v) => v.id === d.node.data?.vlanRef);
    if (vlan) c += vcell(idgen('pdot'), lw - 16, P.TOP_PAD + 3, 9, 9, `ellipse;html=1;fillColor=${vlan.color};strokeColor=#ffffff;strokeWidth=1;`, cont);

    const name = d.node.data?.label || deviceLabel(d.node.data?.deviceType, lang);
    const nameLines = wrapText(name, P.NAME_CHARS, P.NAME_MAX_LINES);
    const nameTop = P.TOP_PAD + P.ICON + 6;
    const nameH = Math.max(P.NAME_LH, (nameLines.length || 1) * P.NAME_LH);
    c += vcell(idgen('pname'), 6, nameTop, lw - 10, nameH, `text;html=1;align=left;verticalAlign=top;fontSize=${P.NAME_FS};fontStyle=1;fontColor=${INK};fontFamily=${SANS};spacingLeft=2;`, cont, nameLines.join('<br>'));
    if (d.node.data?.ip) {
      const ipY = nameTop + (nameLines.length || 1) * P.NAME_LH + 4;
      c += vcell(idgen('pip'), 6, ipY, lw - 10, P.IP_LH + 2, `text;html=1;align=left;verticalAlign=top;fontSize=10;fontColor=${MUTED};fontFamily=${MONO};spacingLeft=2;`, cont, d.node.data.ip);
    }

    const portColors = portPeerVlanColors(d.node, doc);
    L.groups.forEach((g) => {
      const py = g.rows === 2 ? (L.topRowY + L.bottomRowY + P.CELL_H) / 2 : L.topRowY + P.CELL_H / 2;
      c += vcell(idgen('ppfx'), g.x, py - 7, P.PREFIX_W, 14, `text;html=1;align=center;verticalAlign=middle;fontSize=9;fontStyle=1;fontColor=${MUTED};fontFamily=${MONO};`, cont, g.prefix || '\u2014');
      g.cells.forEach((cell) => {
        const pid = idgen('pcell');
        portCell[d.deviceId + ':' + cell.port.id] = pid;
        sideOf[d.deviceId + ':' + cell.port.id] = cell.side;
        const col = portColors[cell.port.id];
        const fill = col || '#F4F5F3';
        const stroke = col || FRAME;
        const num = cell.port.num != null ? cell.port.num : cell.port.name;
        c += vcell(pid, cell.x, cell.y, cell.w, cell.h, `rounded=1;arcSize=20;html=1;whiteSpace=wrap;fillColor=${fill};strokeColor=${stroke};strokeWidth=${col ? 1.4 : 1};fontSize=10;fontColor=#2A3138;fontFamily=${MONO};`, cont, String(num));
      });
    });

    if (!L.groups.length) {
      c += vcell(idgen('pnop'), lw, h / 2 - 8, w - lw, 16, `text;html=1;align=center;verticalAlign=middle;fontSize=11;fontColor=${MUTED};fontFamily=${SANS};`, cont, lang === 'en' ? 'No ports' : 'Keine Ports');
    }
  });

  const anchorPt = (deviceId, portId, peerId) => {
    const a = layouts[deviceId]?.anchors?.[portId];
    if (a?.dual) {
      // Patch panel: dock to TOP/BOTTOM nearest the peer (by vertical position).
      const dev = model.devices.find((x) => x.deviceId === deviceId);
      const peer = peerId ? model.devices.find((x) => x.deviceId === peerId) : null;
      const peerL = peer ? layouts[peerId] : null;
      const peerCenterY = peer
        ? Math.round(peer.position.y) + (peerL?.height || 0) / 2
        : Math.round(dev.position.y) + (a.topY + a.bottomY) / 2;
      const side = pickBothSidesSide(dev.position, a, peerCenterY);
      return side === 'bottom' ? { x: 0.5, y: 1 } : { x: 0.5, y: 0 };
    }
    return sideOf[deviceId + ':' + portId] === 'top' ? { x: 0.5, y: 0 } : { x: 0.5, y: 1 };
  };
  const edgeStyle = (ct, se, te) => `edgeStyle=orthogonalEdgeStyle;rounded=0;html=1;endArrow=none;exitX=${se.x};exitY=${se.y};exitDx=0;exitDy=0;entryX=${te.x};entryY=${te.y};entryDx=0;entryDy=0;${connDrawioStyle(ct)}`;

  model.internalEdges.forEach((e) => {
    const s = portCell[e.sourceDeviceId + ':' + e.sourcePortId];
    const t = portCell[e.targetDeviceId + ':' + e.targetPortId];
    if (!s || !t) return;
    c += connectEdge(idgen('pe'), edgeStyle(e.connType, anchorPt(e.sourceDeviceId, e.sourcePortId, e.targetDeviceId), anchorPt(e.targetDeviceId, e.targetPortId, e.sourceDeviceId)), s, t);
  });

  if (page.showStubs) {
    model.stubs.forEach((st) => {
      const s = portCell[st.deviceId + ':' + st.portId];
      const d = model.devices.find((x) => x.deviceId === st.deviceId);
      const L = layouts[st.deviceId];
      if (!s || !d || !L || !L.anchors[st.portId]) return;
      const a = L.anchors[st.portId];
      const oName = deviceDisplayName(doc, st.otherDeviceId, lang);
      const oPort = portNameGlobal(doc, st.otherDeviceId, st.otherPortId);
      const bw = 120;
      const bh = oPort ? 30 : 19;
      const html = `<span style="font-size:9px;font-weight:600;color:${INK}">${esc(oName)}</span>` + (oPort ? `<br><span style="font-size:9px;color:${MUTED};font-family:${MONO},monospace">${esc(oPort)}</span>` : '');
      const boxId = idgen('pstub');
      if (a.dual) {
        // Patch panel: vertical stub + box BELOW the port (bottom side).
        const ax = Math.round(d.position.x) + a.anchorX;
        const ay = Math.round(d.position.y) + a.bottomY;
        const bx = ax - bw / 2;
        const by = ay + 50;
        c += vcell(boxId, bx, by, bw, bh, `rounded=1;arcSize=8;html=1;dashed=1;dashPattern=3 2;fillColor=#ffffff;strokeColor=${MUTED};align=left;verticalAlign=middle;spacingLeft=6;fontFamily=${SANS};`, '1', html);
        c += connectEdge(idgen('pse'), edgeStyle(st.connType, { x: 0.5, y: 1 }, { x: 0.5, y: 0 }), s, boxId);
        return;
      }
      const side = sideOf[st.deviceId + ':' + st.portId] || 'bottom';
      const ax = Math.round(d.position.x) + a.x;
      const ay = Math.round(d.position.y) + a.y;
      const off = side === 'top' ? -50 : 50;
      const bx = ax - bw / 2;
      const by = side === 'top' ? ay + off - bh : ay + off;
      c += vcell(boxId, bx, by, bw, bh, `rounded=1;arcSize=8;html=1;dashed=1;dashPattern=3 2;fillColor=#ffffff;strokeColor=${MUTED};align=left;verticalAlign=middle;spacingLeft=6;fontFamily=${SANS};`, '1', html);
      const se = side === 'top' ? { x: 0.5, y: 0 } : { x: 0.5, y: 1 };
      const te = side === 'top' ? { x: 0.5, y: 1 } : { x: 0.5, y: 0 };
      c += connectEdge(idgen('pse'), edgeStyle(st.connType, se, te), s, boxId);
    });
  }
  return c;
}

export function detailCells(doc, page, lang, pageInfo, idgen) {
  return page.viewMode === 'panel'
    ? detailPanelCells(doc, page, lang, pageInfo, idgen)
    : detailListCells(doc, page, lang, pageInfo, idgen);
}

// ---- Location plan ("Positionsplan") --------------------------------------
// Native, editable draw.io cells: a background image (style-safe via
// safeImageUri), device-icon markers + labels, and straight connection edges
// between markers. The frame group carries netappLocation so the SwitchR
// importer skips the native cells and rebuilds the live page from the meta cell.
export function locationCells(doc, page, lang, pageInfo, idgen) {
  const innerW = SHEET.w - 2 * INNER;
  const innerH = SHEET.h - 2 * INNER;
  let c = frameCells(doc, lang, pageInfo, pageDisplayName(page.name, lang), usedConnTypes(doc), idgen, `netappLocation="${esc(page.id)}"`);
  const bg = page.background || {};
  if (bg.dataUri) {
    c += imageCell(idgen('locbg'), INNER + 1, INNER + 1, innerW - 2, innerH - 2, bg.dataUri, '1');
  }
  // Element zoom scales ONLY the markers; background + frame stay fixed.
  const s = page.contentScale || 1;
  const MW = 46 * s;
  const labelFont = Math.max(9, Math.round(10 * s));
  const markers = (page.markers || [])
    .map((m) => ({ m, node: findDeviceGlobal(doc, m.deviceId) }))
    .filter((x) => x.node);
  const markerCell = {};
  markers.forEach(({ m, node }) => {
    const id = idgen('locm');
    markerCell[m.deviceId] = id;
    const x = Math.round(m.position?.x || 0);
    const y = Math.round(m.position?.y || 0);
    c += imageCell(id, x, y, MW, MW, iconDataUri(node.data?.deviceType), '1');
    const name = node.data?.label || deviceLabel(node.data?.deviceType, lang);
    c += vcell(idgen('locl'), x - 30, y + MW + 1, MW + 60, 16, `text;html=1;align=center;verticalAlign=top;fontSize=${labelFont};fontStyle=1;fontColor=${INK};fontFamily=${SANS};`, '1', name);
  });
  if (page.showConnections !== false) {
    const set = new Set(markers.map((x) => x.m.deviceId));
    getAllEdges(doc).forEach((e) => {
      if (set.has(e.source) && set.has(e.target)) {
        const s = markerCell[e.source];
        const t = markerCell[e.target];
        if (s && t) c += connectEdge(idgen('loce'), `edgeStyle=none;rounded=0;html=1;endArrow=none;${connDrawioStyle(e.connType)}`, s, t);
      }
    });
  }
  return c;
}

// ---- Auto page: Logical diagram -------------------------------------------
export function logicalCells(doc, lang, pageInfo, idgen) {
  const heading = lang === 'en' ? 'Logical Network Diagram' : 'Logisches Netzwerkdiagramm';
  let c = frameCells(doc, lang, pageInfo, heading, usedConnTypes(doc), idgen, `netappAuto="logical"`);
  const devices = allDevices(doc);
  const innerW = SHEET.w - 2 * INNER;
  const spineX = INNER + 26;
  const boxX = INNER + 72;
  const boxW = innerW - 72 - 16;
  const cellW = 156;
  const cellH = 104;
  let y = INNER + 70;

  if (!devices.length) {
    c += vcell(idgen('empty'), SHEET.w / 2 - 200, SHEET.h / 2 - 15, 400, 30, `text;html=1;align=center;verticalAlign=middle;fontSize=18;fontColor=${MUTED};fontFamily=${SANS};`, '1', lang === 'en' ? 'No devices yet.' : 'Noch keine Ger\u00e4te vorhanden.');
    return c;
  }

  const groups = [];
  doc.vlans
    .slice()
    .sort((a, b) => a.vlanId - b.vlanId)
    .forEach((v) => groups.push({ vlan: v, items: devices.filter((d) => d.data?.vlanRef === v.id) }));
  const unassigned = devices.filter((d) => !doc.vlans.some((v) => v.id === d.data?.vlanRef));
  if (unassigned.length) groups.push({ vlan: null, items: unassigned });

  const spineTop = y;
  let spineBottom = y;
  const boxOps = [];
  groups.forEach((g) => {
    if (!g.items.length) return;
    const perRow = Math.max(1, Math.floor((boxW - 24) / cellW));
    const rows = Math.ceil(g.items.length / perRow);
    const headerH = 40;
    const boxH = headerH + rows * cellH + 14;
    boxOps.push({ g, by: y, boxH, perRow, headerH, midY: y + boxH / 2 });
    spineBottom = y + boxH / 2;
    y += boxH + 22;
  });

  // spine + L3 marker
  c += lineEdge(idgen('spine'), spineX, spineTop - 6, spineX, spineBottom, `endArrow=none;html=1;strokeColor=${FRAME};strokeWidth=2;`, '1');
  c += vcell(idgen('l3'), spineX - 34, spineTop - 40, 68, 26, `rounded=1;arcSize=20;html=1;fillColor=${FRAME};strokeColor=none;`, '1');
  c += vcell(idgen('l3t'), spineX - 34, spineTop - 40, 68, 26, `text;html=1;align=center;verticalAlign=middle;fontSize=11;fontStyle=1;fontColor=#ffffff;fontFamily=${SANS};`, '1', 'L3 / Core');

  boxOps.forEach(({ g, by, boxH, perRow, headerH, midY }) => {
    const color = g.vlan ? g.vlan.color : MUTED;
    const title = g.vlan ? `VLAN ${g.vlan.vlanId} \u00b7 ${g.vlan.name}` : lang === 'en' ? 'Unassigned' : 'Ohne VLAN';
    const net = g.vlan && g.vlan.network ? g.vlan.network : '';
    c += lineEdge(idgen('conn'), spineX, midY, boxX, midY, `endArrow=none;html=1;strokeColor=${FRAME};strokeWidth=1.2;`, '1');
    c += vcell(idgen('gbox'), boxX, by, boxW, boxH, `rounded=1;arcSize=6;html=1;fillColor=#ffffff;strokeColor=${FRAME};strokeWidth=1.2;`, '1');
    c += vcell(idgen('ghdr'), boxX, by, boxW, headerH, `rounded=1;arcSize=6;html=1;fillColor=${color};opacity=8;strokeColor=none;`, '1');
    c += vcell(idgen('gdot'), boxX + 12, by + headerH / 2 - 6, 12, 12, `ellipse;html=1;fillColor=${color};strokeColor=none;`, '1');
    c += vcell(idgen('gtitle'), boxX + 30, by + 8, boxW - 200, 24, `text;html=1;align=left;verticalAlign=middle;fontSize=14;fontStyle=1;fontColor=${INK};fontFamily=${SANS};`, '1', title);
    if (net) c += vcell(idgen('gnet'), boxX + boxW - 180, by + 8, 166, 24, `text;html=1;align=right;verticalAlign=middle;fontSize=12;fontColor=${MUTED};fontFamily=${MONO};`, '1', net);
    g.items.forEach((dd, i) => {
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const cx = boxX + 14 + col * cellW + cellW / 2;
      const iy = by + headerH + row * cellH + 8;
      c += imageCell(idgen('gi'), cx - 22, iy, 44, 44, iconDataUri(dd.data?.deviceType), '1');
      const nm = dd.data?.label || deviceLabel(dd.data?.deviceType, lang);
      c += vcell(idgen('gnm'), cx - cellW / 2, iy + 46, cellW, 16, `text;html=1;align=center;verticalAlign=middle;fontSize=11;fontStyle=1;fontColor=${INK};fontFamily=${SANS};`, '1', nm);
      if (dd.data?.ip) c += vcell(idgen('gip'), cx - cellW / 2, iy + 62, cellW, 14, `text;html=1;align=center;verticalAlign=middle;fontSize=9;fontColor=${MUTED};fontFamily=${MONO};`, '1', dd.data.ip);
    });
  });
  return c;
}

// ---- Auto page: Cable list (native draw.io tables) ------------------------
export function cableListCells(doc, lang, pageInfo, idgen) {
  const heading = lang === 'en' ? 'Cable List' : 'Kabelliste';
  let c = frameCells(doc, lang, pageInfo, heading, null, idgen, `netappAuto="cablelist"`);
  const innerW = SHEET.w - 2 * INNER;
  const x0 = INNER + 16;
  const tableW = innerW - 32;
  const colFrom = Math.round(tableW * 0.2);
  const colTo = Math.round(tableW * 0.2);
  const colName = Math.round(tableW * 0.15);
  const colUnt = Math.round(tableW * 0.11);
  const colTag = Math.round(tableW * 0.14);
  const colLag = Math.round(tableW * 0.1);
  const colPage = tableW - colFrom - colTo - colName - colUnt - colTag - colLag;
  const xFrom = 0;
  const xTo = colFrom;
  const xName = colFrom + colTo;
  const xUnt = xName + colName;
  const xTag = xUnt + colUnt;
  const xLag = xTag + colTag;
  const xPage = xLag + colLag;
  const headerH = 30;
  const rowH = 26;
  let y = INNER + 70;

  const dataStyle = `shape=partialRectangle;html=1;whiteSpace=wrap;connectable=0;overflow=hidden;strokeColor=${FRAME};fillColor=none;align=left;verticalAlign=middle;spacingLeft=8;fontSize=12;fontColor=${INK};fontFamily=${SANS};top=0;left=0;bottom=1;right=1;`;
  const headStyle = `shape=partialRectangle;html=1;whiteSpace=wrap;connectable=0;overflow=hidden;strokeColor=${FRAME};fillColor=#f2f2f0;align=left;verticalAlign=middle;spacingLeft=8;fontSize=10;fontStyle=1;fontColor=${MUTED};fontFamily=${SANS};top=0;left=0;bottom=1;right=1;`;
  const vlanStyle = `shape=partialRectangle;html=1;whiteSpace=wrap;connectable=0;overflow=hidden;strokeColor=${FRAME};fillColor=none;align=center;verticalAlign=middle;fontSize=12;fontColor=${INK};fontFamily=${MONO};top=0;left=0;bottom=1;right=1;`;
  const warnStyle = `shape=partialRectangle;html=1;whiteSpace=wrap;connectable=0;overflow=hidden;strokeColor=${FRAME};fillColor=none;align=center;verticalAlign=middle;fontSize=11;fontStyle=1;fontColor=${WARN};fontFamily=${SANS};top=0;left=0;bottom=1;right=1;`;

  const fmt = (devId, portId) => {
    const nm = deviceDisplayName(doc, devId, lang);
    const pn = portNameGlobal(doc, devId, portId);
    return pn ? `${nm}  \u00b7  ${pn}` : nm;
  };

  // Per-port VLAN resolution (cached per device) to derive the Untagged/Tagged
  // columns + Design-Check conflicts. Mirrors cableSections() in svgExport.js.
  const resolvedCache = {};
  const resolveFor = (devId) => {
    if (!(devId in resolvedCache)) {
      const node = findDeviceGlobal(doc, devId);
      resolvedCache[devId] = node ? resolvePortVlans(node, doc) : {};
    }
    return resolvedCache[devId];
  };
  const conflictLabel = lang === 'en' ? '\u26A0 Conflict' : '\u26A0 Konflikt';
  const vlanInfo = (e) => {
    const ra = resolveFor(e.source)[e.sourcePortId] || {};
    const rb = resolveFor(e.target)[e.targetPortId] || {};
    const uaId = ra.untaggedVlan ? ra.untaggedVlan.vlanId : null;
    const ubId = rb.untaggedVlan ? rb.untaggedVlan.vlanId : null;
    const untaggedConflict = uaId != null && ubId != null && uaId !== ubId;
    let untaggedVal = '';
    if (!untaggedConflict) {
      const id = uaId != null ? uaId : ubId;
      if (id != null) untaggedVal = String(id);
    }
    const ta = (ra.taggedVlans || []).map((v) => v.vlanId).sort((x, z) => x - z);
    const tb = (rb.taggedVlans || []).map((v) => v.vlanId).sort((x, z) => x - z);
    const taggedConflict = ta.length > 0 && tb.length > 0 && ta.join(',') !== tb.join(',');
    let taggedVal = '';
    if (!taggedConflict) taggedVal = (ta.length ? ta : tb).join(', ');
    return { untaggedVal, untaggedConflict, taggedVal, taggedConflict };
  };

  // group cables by medium (connType)
  const lagById = {};
  (doc.pages || []).forEach((p) => (p.lags || []).forEach((l) => (lagById[l.id] = l)));
  const byType = {};
  (doc.pages || []).forEach((p) => {
    if (p.type === 'detail' || p.type === 'location') return;
    (p.edges || []).forEach((e) => {
      const key = e.connType || 'ethernet';
      (byType[key] = byType[key] || []).push({ e, page: p.name });
    });
  });
  const keys = Object.keys(byType).sort((a, b) => connLabel(a, lang).localeCompare(connLabel(b, lang)));

  keys.forEach((key) => {
    const items = byType[key];
    const ct = connType(key);
    const color = (ct && ct.color) || MUTED;
    const secH = 40;
    c += vcell(idgen('sec'), x0, y, tableW, secH, `rounded=1;arcSize=8;html=1;fillColor=${color};opacity=8;strokeColor=${FRAME};strokeWidth=1;align=left;verticalAlign=middle;spacingLeft=52;fontSize=14;fontStyle=1;fontColor=${INK};fontFamily=${SANS};`, '1', `${connLabel(key, lang)}  (${items.length})`);
    c += vcell(idgen('sw'), x0 + 16, y + secH / 2 - 2, 28, 4, `rounded=1;fillColor=${color};strokeColor=none;`, '1');
    y += secH + 6;

    const rows = items.length || 1;
    const tableH = headerH + rows * rowH;
    const tid = idgen('tbl');
    c += `<mxCell id="${tid}" value="" style="shape=table;childLayout=tableLayout;startSize=0;container=1;collapsible=0;fillColor=none;strokeColor=${FRAME};strokeWidth=1;html=1;" vertex="1" parent="1"><mxGeometry x="${x0}" y="${y}" width="${tableW}" height="${tableH}" as="geometry"/></mxCell>`;

    const hrow = idgen('trow');
    c += `<mxCell id="${hrow}" value="" style="shape=tableRow;horizontal=0;startSize=0;collapsible=0;fillColor=#f2f2f0;strokeColor=${FRAME};html=1;" vertex="1" parent="${tid}"><mxGeometry x="0" y="0" width="${tableW}" height="${headerH}" as="geometry"/></mxCell>`;
    c += tableCell(idgen('tc'), xFrom, colFrom, headerH, headStyle, lang === 'en' ? 'FROM (DEVICE \u00b7 PORT)' : 'VON (GER\u00c4T \u00b7 PORT)', hrow);
    c += tableCell(idgen('tc'), xTo, colTo, headerH, headStyle, lang === 'en' ? 'TO (DEVICE \u00b7 PORT)' : 'NACH (GER\u00c4T \u00b7 PORT)', hrow);
    c += tableCell(idgen('tc'), xName, colName, headerH, headStyle, lang === 'en' ? 'LABEL' : 'BEZEICHNUNG', hrow);
    c += tableCell(idgen('tc'), xUnt, colUnt, headerH, headStyle, 'UNTAGGED', hrow);
    c += tableCell(idgen('tc'), xTag, colTag, headerH, headStyle, 'TAGGED', hrow);
    c += tableCell(idgen('tc'), xLag, colLag, headerH, headStyle, 'LAG', hrow);
    c += tableCell(idgen('tc'), xPage, colPage, headerH, headStyle, lang === 'en' ? 'PAGE' : 'SEITE', hrow);

    items.forEach((it, i) => {
      const r = idgen('trow');
      const rowFill = i % 2 === 1 ? '#fafafa' : 'none';
      const lagName = it.e.lagId ? lagById[it.e.lagId]?.name || 'LAG' : '';
      const u = vlanInfo(it.e);
      c += `<mxCell id="${r}" value="" style="shape=tableRow;horizontal=0;startSize=0;collapsible=0;fillColor=${rowFill};strokeColor=${FRAME};html=1;" vertex="1" parent="${tid}"><mxGeometry x="0" y="${headerH + i * rowH}" width="${tableW}" height="${rowH}" as="geometry"/></mxCell>`;
      c += tableCell(idgen('tc'), xFrom, colFrom, rowH, dataStyle, fmt(it.e.source, it.e.sourcePortId), r);
      c += tableCell(idgen('tc'), xTo, colTo, rowH, dataStyle, fmt(it.e.target, it.e.targetPortId), r);
      c += tableCell(idgen('tc'), xName, colName, rowH, dataStyle, it.e.name || '', r);
      c += u.untaggedConflict
        ? tableCell(idgen('tc'), xUnt, colUnt, rowH, warnStyle, conflictLabel, r)
        : tableCell(idgen('tc'), xUnt, colUnt, rowH, vlanStyle, u.untaggedVal, r);
      c += u.taggedConflict
        ? tableCell(idgen('tc'), xTag, colTag, rowH, warnStyle, conflictLabel, r)
        : tableCell(idgen('tc'), xTag, colTag, rowH, vlanStyle, u.taggedVal, r);
      c += tableCell(idgen('tc'), xLag, colLag, rowH, dataStyle, lagName, r);
      c += tableCell(idgen('tc'), xPage, colPage, rowH, dataStyle, it.page || '', r);
    });
    y += tableH + 22;
  });

  if (!keys.length) {
    c += vcell(idgen('empty'), SHEET.w / 2 - 200, SHEET.h / 2 - 15, 400, 30, `text;html=1;align=center;verticalAlign=middle;fontSize=18;fontColor=${MUTED};fontFamily=${SANS};`, '1', lang === 'en' ? 'No connections yet.' : 'Noch keine Verbindungen.');
  }
  return c;
}


// ---- Auto page: VLAN list (native draw.io tables) --------------------------
export function vlanListCells(doc, lang, pageInfo, idgen) {
  const heading = lang === 'en' ? 'VLAN List' : 'VLAN-Liste';
  let c = frameCells(doc, lang, pageInfo, heading, null, idgen, `netappAuto="vlanlist"`);
  const innerW = SHEET.w - 2 * INNER;
  const x0 = INNER + 16;
  const tableW = innerW - 32;
  const colName = Math.round(tableW * 0.3);
  const colPort = Math.round(tableW * 0.22);
  const colIp = Math.round(tableW * 0.24);
  const colType = tableW - colName - colPort - colIp;
  const headerH = 30;
  const rowH = 26;
  let y = INNER + 70;
  const entries = deviceIpEntries(doc, lang);

  const dataStyle = (mono) => `shape=partialRectangle;html=1;whiteSpace=wrap;connectable=0;overflow=hidden;strokeColor=${FRAME};fillColor=none;align=left;verticalAlign=middle;spacingLeft=8;fontSize=12;fontColor=${INK};fontFamily=${mono ? MONO : SANS};top=0;left=0;bottom=1;right=1;`;
  const headStyle = `shape=partialRectangle;html=1;whiteSpace=wrap;connectable=0;overflow=hidden;strokeColor=${FRAME};fillColor=#f2f2f0;align=left;verticalAlign=middle;spacingLeft=8;fontSize=10;fontStyle=1;fontColor=${MUTED};fontFamily=${SANS};top=0;left=0;bottom=1;right=1;`;

  const renderSection = (vlan, items) => {
    const color = vlan ? vlan.color : MUTED;
    const info = vlan && vlan.network ? subnetInfo(vlan.network) : null;
    const secH = 52;
    const title = vlan ? `VLAN ${vlan.vlanId} \u00b7 ${vlan.name}` : lang === 'en' ? 'Unassigned devices' : 'Ger\u00e4te ohne VLAN';
    let metaTxt = '';
    if (vlan) {
      const meta = [];
      if (vlan.network) meta.push(vlan.network);
      if (info && info.valid) meta.push((lang === 'en' ? 'usable ' : 'nutzbar ') + info.usable);
      if (info && info.valid) meta.push(info.firstHost + ' \u2013 ' + info.lastHost);
      if (vlan.dhcpStart && vlan.dhcpEnd) meta.push('DHCP ' + vlan.dhcpStart + ' \u2013 ' + vlan.dhcpEnd);
      metaTxt = meta.join('   \u2502   ');
    }
    const secHtml = `<span style="font-size:14px;font-weight:600;color:${INK}">${esc(title)}</span>` + (metaTxt ? `<br><span style="font-size:11px;color:${MUTED};font-family:${MONO},monospace">${esc(metaTxt)}</span>` : '');
    c += vcell(idgen('sec'), x0, y, tableW, secH, `rounded=1;arcSize=6;html=1;fillColor=${color};opacity=8;strokeColor=${FRAME};strokeWidth=1;align=left;verticalAlign=middle;spacingLeft=40;fontFamily=${SANS};`, '1', secHtml);
    c += vcell(idgen('secdot'), x0 + 14, y + secH / 2 - 6, 12, 12, `ellipse;html=1;fillColor=${color};strokeColor=none;`, '1');
    y += secH + 6;

    const rows = items.length || 1;
    const tableH = headerH + rows * rowH;
    const tid = idgen('tbl');
    c += `<mxCell id="${tid}" value="" style="shape=table;childLayout=tableLayout;startSize=0;container=1;collapsible=0;fillColor=none;strokeColor=${FRAME};strokeWidth=1;html=1;" vertex="1" parent="1"><mxGeometry x="${x0}" y="${y}" width="${tableW}" height="${tableH}" as="geometry"/></mxCell>`;

    const hrow = idgen('trow');
    c += `<mxCell id="${hrow}" value="" style="shape=tableRow;horizontal=0;startSize=0;collapsible=0;fillColor=#f2f2f0;strokeColor=${FRAME};html=1;" vertex="1" parent="${tid}"><mxGeometry x="0" y="0" width="${tableW}" height="${headerH}" as="geometry"/></mxCell>`;
    c += tableCell(idgen('tc'), 0, colName, headerH, headStyle, lang === 'en' ? 'DEVICE' : 'GER\u00c4T', hrow);
    c += tableCell(idgen('tc'), colName, colPort, headerH, headStyle, 'PORT / INTERFACE', hrow);
    c += tableCell(idgen('tc'), colName + colPort, colIp, headerH, headStyle, 'IP', hrow);
    c += tableCell(idgen('tc'), colName + colPort + colIp, colType, headerH, headStyle, lang === 'en' ? 'TYPE / PAGE' : 'TYP / SEITE', hrow);

    if (!items.length) {
      const r = idgen('trow');
      c += `<mxCell id="${r}" value="" style="shape=tableRow;horizontal=0;startSize=0;collapsible=0;fillColor=none;strokeColor=${FRAME};html=1;" vertex="1" parent="${tid}"><mxGeometry x="0" y="${headerH}" width="${tableW}" height="${rowH}" as="geometry"/></mxCell>`;
      c += tableCell(idgen('tc'), 0, colName, rowH, dataStyle(true), '\u2014', r);
      c += tableCell(idgen('tc'), colName, colPort, rowH, dataStyle(true), '', r);
      c += tableCell(idgen('tc'), colName + colPort, colIp, rowH, dataStyle(true), '', r);
      c += tableCell(idgen('tc'), colName + colPort + colIp, colType, rowH, dataStyle(false), '', r);
    } else {
      items.forEach((it, i) => {
        const r = idgen('trow');
        const rowFill = i % 2 === 1 ? '#fafafa' : 'none';
        c += `<mxCell id="${r}" value="" style="shape=tableRow;horizontal=0;startSize=0;collapsible=0;fillColor=${rowFill};strokeColor=${FRAME};html=1;" vertex="1" parent="${tid}"><mxGeometry x="0" y="${headerH + i * rowH}" width="${tableW}" height="${rowH}" as="geometry"/></mxCell>`;
        const nm = it.label || deviceLabel(it.deviceType, lang);
        c += tableCell(idgen('tc'), 0, colName, rowH, dataStyle(false), nm, r);
        c += tableCell(idgen('tc'), colName, colPort, rowH, dataStyle(true), it.port || '', r);
        c += tableCell(idgen('tc'), colName + colPort, colIp, rowH, dataStyle(true), it.ip || '\u2014', r);
        c += tableCell(idgen('tc'), colName + colPort + colIp, colType, rowH, dataStyle(false), deviceLabel(it.deviceType, lang) + '  \u00b7  ' + (it.page || ''), r);
      });
    }
    y += tableH + 22;
  };

  const renderZoneHeader = (label) => {
    const zh = 30;
    c += vcell(idgen('zone'), x0, y, tableW, zh, `text;html=1;align=left;verticalAlign=middle;fontSize=12;fontStyle=1;fontColor=${INK};fontFamily=${SANS};spacingLeft=2;`, '1', `<span style="letter-spacing:1.5px">${('ZONE  \u00b7  ' + (label || '')).toUpperCase()}</span>`);
    y += zh;
  };

  if (doc.zonesEnabled) {
    // Group the VLAN sections by zone (zone list order) + a trailing "no zone".
    const zones = doc.zones || [];
    zones.forEach((z) => {
      const zvlans = doc.vlans.filter((v) => v.zoneId === z.id).sort((a, b) => a.vlanId - b.vlanId);
      if (!zvlans.length) return;
      renderZoneHeader(z.name || (lang === 'en' ? 'Zone' : 'Zone'));
      zvlans.forEach((v) => renderSection(v, sortEntriesByIp(entries.filter((e) => e.vlanRef === v.id))));
    });
    const noZone = doc.vlans.filter((v) => !v.zoneId || !zones.some((z) => z.id === v.zoneId)).sort((a, b) => a.vlanId - b.vlanId);
    if (noZone.length) {
      renderZoneHeader(lang === 'en' ? 'No zone' : 'Ohne Zone');
      noZone.forEach((v) => renderSection(v, sortEntriesByIp(entries.filter((e) => e.vlanRef === v.id))));
    }
  } else {
    doc.vlans
      .slice()
      .sort((a, b) => a.vlanId - b.vlanId)
      .forEach((v) => renderSection(v, sortEntriesByIp(entries.filter((e) => e.vlanRef === v.id))));
  }
  const unassigned = sortEntriesByIp(entries.filter((e) => !doc.vlans.some((v) => v.id === e.vlanRef)));
  if (unassigned.length) renderSection(null, unassigned);

  if (!doc.vlans.length && !entries.length) {
    c += vcell(idgen('empty'), SHEET.w / 2 - 200, SHEET.h / 2 - 15, 400, 30, `text;html=1;align=center;verticalAlign=middle;fontSize=18;fontColor=${MUTED};fontFamily=${SANS};`, '1', lang === 'en' ? 'No VLANs yet.' : 'Noch keine VLANs.');
  }
  return c;
}
